import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useMutation } from '@apollo/client/react';
import { usePostHog } from 'posthog-js/react';
import { useTranslations } from 'use-intl';

import { ADD_FORM_MESSAGES_PREVIEW } from '@/manager-graphql';
import {
  captureResponseCompletedOnce,
  captureResponseStartedOnce,
  useIsFunnelTracked,
} from '@/shared-measurement';
import { getPanelRedirectUrl, type ResponderPanel } from '@/shared-survey';
import { ensureUrlProtocol, isSafeHttpUrl } from '@/shared-utilities';
import {
  SUBMIT_FORM,
  ADD_FORM_MESSAGES,
  type GetProjectQuery,
} from '@/survey-graphql';
import { toast } from '@/ui';

import type { AIFormQuestion } from './survey-ai-forms-form-section.types';
import type { AiFormsResponseValues } from './survey-ai-forms-form.hooks';
import {
  areAllRequiredQuestionsAnswered,
  areAllRequiredQuestionsSubmittedAndAnswered,
  buildSubQuestionAnswers,
  formatValueForMessage,
  getQuestionValueAsMatrix,
  getUnansweredRequiredQuestions,
  isMatrixQuestion,
  isRequiredMatrixIncomplete,
  isValidResponseValue,
} from './survey-ai-forms.helpers';
import type { QuestionResponse } from './survey-ai-forms.types';

/**
 * `redirectUrl` has writers that bypass the settings form (temp-project claim,
 * the import flows, the raw API), so the scheme is checked here and not only at
 * the point it was typed.
 */
const leaveForCompletion = (redirectUrl: string | null | undefined) => {
  if (typeof window === 'undefined') return;

  if (redirectUrl && isSafeHttpUrl(redirectUrl)) {
    window.location.href = ensureUrlProtocol(redirectUrl.trim());
    return;
  }

  window.location.reload();
};

interface UseSurveyAiFormsSubmissionFinalProps {
  project: Pick<NonNullable<GetProjectQuery['project']>, 'redirectUrl'>;
  questions: AIFormQuestion[];
  projectId: string;
  form: Pick<UseFormReturn<AiFormsResponseValues>, 'reset'>;
  sessionId: string;
  isPreviewMode: boolean;
  responses: Map<string, QuestionResponse>;
  transcripts: Record<string, string | undefined>;
  submittedQuestionIds: Set<string>;
  setSubmittedQuestionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setCurrentQuestionId: (id: string | null) => void;
  insightId: string | null;
  ensureInsightId: () => Promise<string | null>;
  panel?: ResponderPanel | null;
}

export function useSurveyAiFormsSubmissionFinal({
  project,
  questions,
  projectId,
  form,
  sessionId,
  isPreviewMode,
  responses,
  transcripts,
  submittedQuestionIds,
  setSubmittedQuestionIds,
  setCurrentQuestionId,
  insightId,
  ensureInsightId,
  panel = null,
}: UseSurveyAiFormsSubmissionFinalProps) {
  const t = useTranslations('AIForms.toast');
  const tCommon = useTranslations('AIForms');
  const posthog = usePostHog();
  const isFunnelTracked = useIsFunnelTracked();

  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const [submitForm, { loading: isSubmitting }] = useMutation(SUBMIT_FORM);
  const [addFormMessages, { loading: isBatchingMessages }] =
    useMutation(ADD_FORM_MESSAGES);
  const [addFormMessagesPreview, { loading: isBatchingMessagesPreview }] =
    useMutation(ADD_FORM_MESSAGES_PREVIEW);

  const leaveAfterSubmit = () =>
    leaveForCompletion(
      (panel && getPanelRedirectUrl('COMPLETE', panel)) ?? project?.redirectUrl
    );

  const isBatchingMessagesLoading = isPreviewMode
    ? isBatchingMessagesPreview
    : isBatchingMessages;

  const validationErrors = (() => {
    if (!hasTriedSubmit) {
      return new Set<string>();
    }
    const unansweredQuestions = getUnansweredRequiredQuestions(
      questions,
      responses
    );
    const incompleteMatrixQuestions = questions.filter((question) =>
      isRequiredMatrixIncomplete(question, responses.get(question.id)?.value)
    );
    return new Set([
      ...unansweredQuestions.map((q) => q.id),
      ...incompleteMatrixQuestions.map((q) => q.id),
    ]);
  })();

  const handleSubmit = async () => {
    const allRequiredAnswered = isPreviewMode
      ? areAllRequiredQuestionsAnswered(questions, responses)
      : areAllRequiredQuestionsSubmittedAndAnswered(
          questions,
          submittedQuestionIds,
          responses
        );

    if (!allRequiredAnswered) {
      toast.add({
        title: t('requiredQuestionsError'),
        type: 'error',
      });
      return;
    }

    if (isPreviewMode) {
      setIsSubmitSuccessful(true);

      toast.add({
        title: t('submitSuccess'),
        type: 'success',
      });

      setTimeout(() => {
        form.reset();
        setIsSubmitSuccessful(false);
        setHasTriedSubmit(false);
        setSubmittedQuestionIds(new Set());

        setCurrentQuestionId(questions[0]?.id || null);
      }, 1500);

      return;
    }

    if (!insightId) {
      toast.add({
        title: t('genericError'),
        type: 'error',
      });
      return;
    }

    try {
      await submitForm({
        variables: {
          input: {
            insightId,
            sessionId,
          },
        },
      });

      setIsSubmitSuccessful(true);

      if (isFunnelTracked) {
        captureResponseCompletedOnce(posthog, {
          projectId,
          responseId: insightId,
        });
      }

      toast.add({
        title: t('submitSuccess'),
        type: 'success',
      });

      setTimeout(() => {
        leaveAfterSubmit();
      }, 1500);
    } catch (error: unknown) {
      setIsSubmitSuccessful(false);
      toast.add({
        title:
          error instanceof Error ? error.message : t('submitCatchAllError'),
        type: 'error',
      });
    }
  };

  const handleFormSubmit = async () => {
    setHasTriedSubmit(true);

    const unansweredQuestions = getUnansweredRequiredQuestions(
      questions,
      responses
    );

    const incompleteMatrixQuestions = questions.filter((question) =>
      isRequiredMatrixIncomplete(question, responses.get(question.id)?.value)
    );

    if (
      unansweredQuestions.length > 0 ||
      incompleteMatrixQuestions.length > 0
    ) {
      if (incompleteMatrixQuestions.length > 0) {
        toast.add({ title: t('matrixIncomplete'), type: 'error' });
      }
      return;
    }

    try {
      const answers = questions
        .filter((question) => {
          const response = responses.get(question.id);
          return response !== undefined && isValidResponseValue(response.value);
        })
        .map((question) => {
          const response = responses.get(question.id);
          if (!response || !isValidResponseValue(response.value)) {
            return null;
          }

          const transcript = transcripts[question.id];

          const matrixValue = isMatrixQuestion(question)
            ? getQuestionValueAsMatrix(response.value)
            : undefined;
          const subQuestionAnswers = matrixValue
            ? buildSubQuestionAnswers(matrixValue)
            : undefined;

          return {
            content: formatValueForMessage(
              response.value,
              question,
              tCommon('summary.notApplicable'),
              tCommon('summary.noAnswer')
            ),
            questionId: question.id,
            transcript: transcript || undefined,
            assets: undefined,
            subQuestionAnswers:
              subQuestionAnswers && subQuestionAnswers.length > 0
                ? subQuestionAnswers
                : undefined,
          };
        })
        .filter(
          (answer): answer is NonNullable<typeof answer> => answer !== null
        );

      if (isPreviewMode) {
        await addFormMessagesPreview({
          variables: {
            input: {
              projectId: projectId,
              sessionId,
              answers,
            },
          },
        });

        setIsSubmitSuccessful(true);

        toast.add({
          title: t('submitSuccess'),
          type: 'success',
        });

        setTimeout(() => {
          form.reset();
          setIsSubmitSuccessful(false);
          setHasTriedSubmit(false);
          setSubmittedQuestionIds(new Set());

          setCurrentQuestionId(questions[0]?.id || null);
        }, 1500);

        return;
      } else {
        const currentInsightId = await ensureInsightId();
        if (!currentInsightId) {
          toast.add({
            title: t('genericError'),
            type: 'error',
          });
          return;
        }

        if (isFunnelTracked) {
          captureResponseStartedOnce(posthog, {
            projectId,
            responseId: currentInsightId,
          });
        }

        await addFormMessages({
          variables: {
            input: {
              insightId: currentInsightId,
              sessionId,
              answers,
            },
          },
        });

        await submitForm({
          variables: {
            input: {
              insightId: currentInsightId,
              sessionId,
            },
          },
        });

        setIsSubmitSuccessful(true);

        if (isFunnelTracked) {
          captureResponseCompletedOnce(posthog, {
            projectId,
            responseId: currentInsightId,
          });
        }

        toast.add({
          title: t('submitSuccess'),
          type: 'success',
        });

        setTimeout(() => {
          leaveAfterSubmit();
        }, 1500);
      }
    } catch (error: unknown) {
      setIsSubmitSuccessful(false);
      toast.add({
        title: error instanceof Error ? error?.message : t('genericError'),
        type: 'error',
      });
    }
  };

  return {
    validationErrors,
    hasTriedSubmit,
    isSubmitSuccessful,
    isSubmitting,
    isBatchingMessagesLoading,
    handleSubmit,
    handleFormSubmit,
  };
}
